import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import {
  fetchListingFeeStatus,
  createListingFeeCheckout,
  verifyListingFee,
} from '../../util/api';

// ================ Async thunks ================ //

export const fetchStatusThunk = createAsyncThunk(
  'ListingFeePage/fetchStatus',
  (_arg, { rejectWithValue }) => {
    return fetchListingFeeStatus().catch(e => rejectWithValue(storableError(e)));
  }
);

export const createCheckoutThunk = createAsyncThunk(
  'ListingFeePage/createCheckout',
  (_arg, { rejectWithValue }) => {
    return createListingFeeCheckout().catch(e => rejectWithValue(storableError(e)));
  }
);

export const verifyThunk = createAsyncThunk(
  'ListingFeePage/verify',
  (sessionId, { rejectWithValue }) => {
    return verifyListingFee(sessionId).catch(e => rejectWithValue(storableError(e)));
  }
);

// Backward compatible wrappers
export const requestFetchStatus = () => dispatch => dispatch(fetchStatusThunk()).unwrap();
export const requestCreateCheckout = () => dispatch => dispatch(createCheckoutThunk()).unwrap();
export const requestVerify = sessionId => dispatch => dispatch(verifyThunk(sessionId)).unwrap();

// ================ Reducer ================ //

const initialState = {
  fetchStatusInProgress: false,
  fetchStatusError: null,
  enabled: null,
  paid: null,
  amount: null,
  currency: null,
  createCheckoutInProgress: false,
  createCheckoutError: null,
  verifyInProgress: false,
  verifyError: null,
};

const listingFeePageSlice = createSlice({
  name: 'ListingFeePage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchStatusThunk.pending, state => {
        state.fetchStatusInProgress = true;
        state.fetchStatusError = null;
      })
      .addCase(fetchStatusThunk.fulfilled, (state, action) => {
        state.fetchStatusInProgress = false;
        state.enabled = action.payload?.enabled !== false;
        state.paid = !!action.payload?.paid;
        state.amount = action.payload?.amount ?? null;
        state.currency = action.payload?.currency ?? null;
      })
      .addCase(fetchStatusThunk.rejected, (state, action) => {
        state.fetchStatusInProgress = false;
        state.fetchStatusError = action.payload;
      })
      .addCase(createCheckoutThunk.pending, state => {
        state.createCheckoutInProgress = true;
        state.createCheckoutError = null;
      })
      .addCase(createCheckoutThunk.fulfilled, state => {
        state.createCheckoutInProgress = false;
      })
      .addCase(createCheckoutThunk.rejected, (state, action) => {
        state.createCheckoutInProgress = false;
        state.createCheckoutError = action.payload;
      })
      .addCase(verifyThunk.pending, state => {
        state.verifyInProgress = true;
        state.verifyError = null;
      })
      .addCase(verifyThunk.fulfilled, (state, action) => {
        state.verifyInProgress = false;
        state.paid = !!action.payload?.paid;
        state.amount = action.payload?.amount ?? state.amount;
        state.currency = action.payload?.currency ?? state.currency;
      })
      .addCase(verifyThunk.rejected, (state, action) => {
        state.verifyInProgress = false;
        state.verifyError = action.payload;
      });
  },
});

export default listingFeePageSlice.reducer;

// ================ Load data ================ //

// NOTE: This route is auth-gated, so loadData only runs client-side.
export const loadData = (params, search) => dispatch => {
  const { status, session_id: sessionId } = parse(search) || {};

  // Returning from a successful Stripe Checkout: verify + record the payment.
  if (status === 'success' && sessionId) {
    return dispatch(verifyThunk(sessionId)).then(() => dispatch(fetchStatusThunk()));
  }

  return dispatch(fetchStatusThunk());
};
